// src/config/pageRegistry.js — GES-252 P4a
//
// THE PAGE REGISTRY: every navigable surface in the app, as a two-level tree of
// { page_key, label, parent }. It is the ONE source of truth for
//   • P4a — the rows of the role × page matrix editor (Settings ▸ Roles & Pages), and
//   • P4b — the enforcement pass (menu render filter + the App.jsx page-entry check).
// Both import THIS module. A key that is not here cannot be granted, and a key granted
// here is the same string `currentPage` / the tab state already routes on.
//
// ── KEY SHAPE ────────────────────────────────────────────────────────────────────────
// A page key is the render key: `currentPage` in MainApp (App.jsx `pages` map).
// A tab key is `<page_key>:<tab id>` — the tab's own existing id, verbatim, never its
// label. Bare strings with spaces ('data-entry:Landed Costs') are deliberate: those ARE
// the ids the tab bodies switch on, and inventing a slug here would create a second name
// for the same surface. The server only rejects empty keys, so the composite is safe.
// Labels are display text and may change freely (41119 renamed two without touching an
// id; GES-253 will translate them). NOTHING routes on a label.
//
// ── WHY THIS FILE RESTATES THE ARRAYS INSTEAD OF IMPORTING THEM ──────────────────────
// Every tab array lives in its page module as a NON-exported `const`, three of them are
// declared inside the component body (ActivitiesPage ACT_TABS) and three surfaces have no
// array at all (payment-management, seat-view, Reports' group row are hardcoded buttons).
// Exporting and importing them would (a) require editing a dozen page files for a task
// that must not change runtime behaviour, and (b) create a genuine import cycle —
// SettingsPage → RolesSettings → pageRegistry → SettingsPage. So the tree is restated
// here, and every block carries a `source:` pointer to the array it mirrors so the two
// can be checked against each other. DRIFT IS THE KNOWN COST: adding a tab to a page and
// not adding it here leaves it un-grantable (it fails CLOSED under P4b, which is the safe
// direction, but it is still a bug — update this file in the same commit).
//
// ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────────────────
// • Level 3+ (Reports' 26 leaf reports, Procurement's OrderTabs, SalesOrderDetail's 7).
//   41118 deferred them; they are already enumerable, so promoting them later is a data
//   change in this file, not a refactor.
// • Filter/lens pills that look like tabs but are not navigation: ChartContainer LENS_SETS,
//   the Sanity severity pills, BenchmarksPage's lens picker, PaymentManagement's
//   payable/receivable class toggle. `sanity` and `benchmarks` have NO tab bar.
// • adminOnly: true entries are listed (P4b needs to know the key exists) but are EXCLUDED
//   from the matrix rows — they are gated by is_admin, which the matrix does not govern,
//   and showing them as tickable would promise something a tick cannot deliver.

// ── LEVEL 1 — the pages. Source: components/Navbar.jsx NAV_ITEMS (23) + the three
// drill-down detail views that are render keys in App.jsx's `pages` map but have no menu
// entry (they are reached from their list page and tied back to it by NAV_ITEMS.match).
const PAGES = [
  { page_key: 'dashboard',           label: '📊 Dashboard' },
  { page_key: 'sites-list',          label: '🏢 Sites' },
  { page_key: 'site-detail',         label: '🏢 Site detail', detail: true },
  { page_key: 'farms-list',          label: '🚜 Farms' },
  { page_key: 'farm-detail',         label: '🚜 Farm detail', detail: true },
  { page_key: 'bands-list',          label: '🐦 Bands Old' },
  { page_key: 'band-detail',         label: '🐦 Band detail', detail: true },
  { page_key: 'bands-d',             label: '🐦 Bands' },
  { page_key: 'benchmarks',          label: '🎯 Benchmarks' },
  { page_key: 'data-entry',          label: '✏️ Data Entry' },
  { page_key: 'activities',          label: '✅ Activities' },
  { page_key: 'husbandry',           label: '🐔 Husbandry' },
  { page_key: 'review',              label: '📋 Review & Post' },
  // GES-257 (41142c). Key is `approvals`, NOT `review` — that one is Review & Post and reusing it
  // would collide with a 1275-line page. adminOnly, and it is the FIRST top-level page to carry the
  // flag: the endpoints behind it are p3AdminGate'd, so offering the menu entry to a non-admin only
  // buys a 403. See the flatMap below and Navbar.jsx / App.jsx for the three places that read it.
  { page_key: 'approvals',           label: '🛡️ Approvals', adminOnly: true },
  { page_key: 'procurement',         label: '📦 Procurement' },
  { page_key: 'sales',               label: '🧾 Sales' },
  { page_key: 'sales-price-list',    label: '💲 Sales Price List' },
  { page_key: 'sales-dispatches',    label: '🚚 Sales Dispatches' },   // GES-431 DISP-5b
  { page_key: 'sales-invoices',      label: '🧾 Sales Invoices' },     // GES-409
  { page_key: 'payment-management',  label: '💸 Payments' },
  { page_key: 'alerts',              label: '🚨 Alerts' },
  { page_key: 'forecast',            label: '🔮 Forecast' },
  { page_key: 'engines',             label: '🔬 Engines' },
  { page_key: 'supporting-engines',  label: '🔩 Supporting Engines' },
  { page_key: 'reports',             label: '📋 Reports' },
  { page_key: 'seat-view',           label: '🏠 Seat View' },
  { page_key: 'reallocation',        label: '🔁 Re-Allocation' },
  { page_key: 'sanity',              label: '🔍 Sanity Report' },
  { page_key: 'general-journal',     label: '🧾 General Journal' },
  // GES-64 (42008a) — the correction-log viewer. adminOnly, and for the same reason `approvals`
  // is: the endpoint behind it is getIsAdmin-gated, so offering the menu entry to a {"*": true}
  // "Full access" non-admin would only buy them a 403. No tabs — the page is one surface.
  { page_key: 'audit-log',           label: '🗒️ Audit Log', adminOnly: true },
  // GES-64 (42008b) — the LIFECYCLE log (log 1: entity biography). adminOnly on the same rule as
  // 'audit-log' above: its endpoint is getIsAdmin-gated, so a {"*": true} "Full access" non-admin
  // would only get a 403 from the menu entry. NOTE the PER-BAND biography is NOT admin-gated and is
  // untouched — it renders inside the band/seat/farm pages. This key is the ORG-WIDE stream only.
  { page_key: 'lifecycle-log',       label: '🧬 Lifecycle Log', adminOnly: true },
  // Control & Admin > Language (v0.46): terms, live overrides, review statuses, engine messages. adminOnly like the logs: every /api/i18n/terms route is getIsAdmin-gated.
  { page_key: 'language',            label: '🌐 Language', adminOnly: true },
  { page_key: 'settings',            label: '⚙️ Settings' },
  { page_key: 'changelog',           label: '📋 Changelog' },
];

// ── LEVEL 2 — the tabs, by parent page. `[id, label]`, id verbatim from the source array.
const TABS = {
  // source: dashboard/sites/SiteViewB.jsx TABS
  'site-detail': [
    ['overview', 'Overview'], ['farms', 'Farms'], ['financial', 'Financial'],
    ['expenses', 'Expenses'], ['forecast', 'Forecast'], ['alerts', 'Alerts'],
    ['settings', 'Settings (⚙)'],
  ],
  // source: dashboard/farms/FarmViewB.jsx TABS
  'farm-detail': [
    ['overview', 'Overview'], ['current', 'Current band'], ['occupancy', 'Occupancy'],
    ['financial', 'Financial'], ['expenses', 'Expenses'], ['forecast', 'Forecast'],
    ['alerts', 'Alerts'], ['settings', 'Settings (⚙)'],
  ],
  // source: dashboard/bands/BandViewB.jsx TABS
  'band-detail': [
    ['overview', 'Overview'], ['timeline', 'Timeline'], ['performance', 'Performance'],
    ['financial', 'Financial'], ['expenses', 'Expenses'], ['forecast', 'Forecast'],
    ['operations', 'Data Entry'], ['alerts', 'Alerts'], ['settings', 'Settings (⚙)'],
  ],
  // source: pages/BandsD/BandsViewD.jsx TABS
  'bands-d': [
    ['overview', '📋 Overview'], ['daily', '📅 Daily'], ['timeline', '🗓️ Timeline'],
    ['finance', '💵 Finance'], ['expenses', '📊 Expenses'], ['consumption', '🌾 Consumption'],
    ['performance', '🎯 Performance'], ['forecast', '🔮 Forecast'], ['alerts', '🚨 Alerts'],
  ],
  // source: pages/DataEntryPage.jsx TAB_GROUPS (41130/GES-255 made these {id,label};
  // the id is the old bare string, so these keys are unchanged by that conversion).
  'data-entry': [
    ['Production', 'Production'], ['Consumption', 'Consumption'], ['Mortality', 'Mortality'],
    ['Parameters', "Parameters (Paramètres d'élevage)"],
    ['Purchase', 'Purchase'], ['Sales', 'Sales'], ['Landed Costs', 'Landed Costs'],
    ['Opening', 'Opening'], ['Adjustment', 'Adjustment'], ['Transfer', 'Transfer'],
    ['Expense', 'Expense'], ['Other Revenue', 'Other Revenue'], ['Finance', 'Finance'],
  ],
  // source: pages/ActivitiesPage.jsx ACT_TABS (declared inside the component)
  activities: [
    ['queue', 'Queue'], ['schedules', 'Schedules'], ['history', 'History'], ['todo', 'To-do'],
  ],
  husbandry: [
    ['water_intake', 'Water intake'], ['weighting', 'Weighting'], ['temperature', 'Temperature'], ['droppings_quality', 'Droppings quality'],
  ],
  // source: pages/ProcurementPage.jsx TABS (GROUPS only bins these nine for display)
  procurement: [
    ['overview', 'Overview'], ['git', 'Goods In Transit'], ['orders', 'Orders'],
    ['planned', 'Planned'], ['payments', 'Payments'], ['history', 'History'],
    ['spend', 'Spend'], ['suppliers', 'Suppliers'], ['reports', 'Reports'],
  ],
  // source: pages/SalesPage.jsx SUB_TABS (three carry `reserved: true` — not built yet,
  // listed so the matrix does not have to change when they ship).
  sales: [
    ['overview', 'Overview (reserved)'], ['pipeline', 'Pipeline (reserved)'],
    ['orders', 'Orders'], ['quotes', 'Quotes'],
  ],
  'sales-price-list': [
    ['sale-prices', 'Sale prices'], ['customer-prices', 'Customer prices'], ['discounts', 'Discounts'],
  ],
  // source: pages/Sales/DispatchesPage.jsx (the shell tab row: Current = draft+open, Completed = delivered+cancelled)
  'sales-dispatches': [['current', 'Current'], ['completed', 'Completed'], ['settings', 'Settings']],
  // source: pages/Sales/InvoicesPage.jsx (REGISTER_TABS in invoicing/invoicingModel.mjs)
  'sales-invoices': [['to-invoice', 'To invoice'], ['drafts', 'Drafts'], ['issued', 'Issued'], ['proformas', 'Proformas'], ['credit-notes', 'Credit notes'], ['documents', 'Documents']],
  // source: pages/PaymentManagementPage.jsx — the `toggleBtn` row (viewMode). HARDCODED
  // there, so these four ids come from the call sites, not from an array.
  'payment-management': [
    ['payment', 'Per payment'], ['order', 'Per order'],
    ['history', 'Credit history'], ['reconcile', 'Reconciliation'],
  ],
  // source: pages/Language/LanguagePage.jsx (shell tabs; the page is admin-only)
  language: [['terms', 'Terms', true], ['engine', 'Engine messages', true]],
  // source: pages/EnginesPage.jsx TABS
  engines: [
    ['e1', 'E1 Stock Ledger'], ['e2', 'E2 Stock On-Hand'], ['e3', 'E3 Egg Freshness'],
    ['e4', 'E4 Consumption Cost'], ['e5', 'E5 Birds History'], ['e6', 'E6 Band Cost'],
    ['e7', 'E7 Band Profit'], ['e8', 'E8 Forecast · raw'], ['e9', 'E9 Alerts · raw'],
    ['e10', 'E10 Orders · raw'], ['e11', 'E11 Reconciliation'], ['e12', 'E12 Sales Orders'], ['e13', 'E13 Egg FIFO Revenue'],
    ['e14', 'E14 Customer Profit'],
  ],
  // source: pages/SupportingEnginesPage.jsx TABS (keyed `view`, split by `kind`)
  'supporting-engines': [
    ['v_avg_cost', 'Avg Cost'], ['v_band_lifecycle_state', 'Band Lifecycle State'],
    ['v_item_price_point', 'Item Price Points'], ['mv_item_price', 'Item Price State'],   // 43030/43031 — were fail-closed (missing here)
    ['v_value_ledger', 'Value Ledger'], ['v_historical_avg_cost', 'Historical Avg Cost'],
    ['v_payment_allocation_net', 'Payment Allocation'], ['v_alloc_bird_days', 'Alloc Bird-Days'],
    ['v_bird_days', 'Bird-Days'], ['v_band_variable_alloc', 'Variable Alloc'],
    ['v_band_fixed_days', 'Fixed Alloc'], ['v_band_daily_birds', 'Daily Birds'],
    ['v_seat_occupancy', 'Seat Occupancy'], ['v_band_events', 'Band Events (audit)'],
    ['v_band_revenue_footing', 'Revenue Footing'], ['v_seat_tenure_conflicts', 'Tenure Conflicts'],
    ['v_egg_fifo_alloc_detail', 'FIFO Alloc Detail'], ['v_bybirds_alloc_detail', 'By-Birds Alloc Detail'],
    ['band_tenures', 'Band Tenures (table)'], ['band_queue', 'Band Queue (table)'],
    ['seat_idle_periods', 'Seat Idle Periods (table)'],
    // 43014/42151: these tabs were defined in SupportingEnginesPage but MISSING here → useVisibleTabs is
    // fail-closed, so they never rendered. Register them so they show.
    ['v_band_lifecycle_pnl', 'Band Lifecycle Profitability'], ['v_band_week_economics', 'Band Week Economics'],
    ['v_band_consumption_weekly', 'Band Consumption (weekly)'], ['band_milestone', 'Band Milestones (table)'],
    ['band_alignment', 'Band Alignment (Tier-2)'],
    ['evaluation_ledger', 'Evaluation Ledger (inspection)'],   // 42131 Part B
    ['v_customer_profit', 'E14 Customer Profit'], ['v_customer_profit_lines', 'Customer Profit Lines'],   // E14 (GES-19) sell-side profit engine
  ],
  // source: pages/ReportsPage.jsx — the group row (an inline tuple literal). The 26 leaf
  // reports under these three are level 3 and DEFERRED (see the header note).
  reports: [
    ['transactions', '📝 Transactions'], ['analytics', '📊 Analytics'], ['finance', '📒 Finance'],
  ],
  // source: App.jsx SeatPage — the `tabBtn` pair. HARDCODED there.
  'seat-view': [['current', 'Current'], ['history', 'History']],
  // source: pages/ExpenseReallocationPage.jsx NATURE_TABS (keyed `key`)
  reallocation: [['Expense', 'Expense'], ['Other Revenue', 'Other Revenue']],
  // source: pages/GeneralJournalPage.jsx TABS — FIVE buttons, plus `idlist`, which has no
  // button and no label: it is set programmatically from a report deep-link and rendered at
  // :312. 41118 flagged it (trap 1) precisely because a registry generated from TABS would
  // miss it and the deep link would then route into an ungoverned surface. It is enumerated
  // from the RENDER SITE, not the array.
  'general-journal': [
    ['account', 'By Account'], ['band', 'By Band'], ['storage', 'By Storage'],
    ['group', 'By Group'], ['integrity', 'Integrity'],
    ['idlist', 'Transaction list (opened from a report link)'],
  ],
  // source: components/Settings/SettingsPage.jsx SETTING_GROUPS, in group order.
  settings: [
    ['bands', '🐔 Bands'], ['farms-dash', '🚜 Farms'], ['sites', '🏢 Sites'],
    ['storage', '🏭 Storage'], ['partners', '👥 Partners'], ['people', '🧑‍🌾 People'],
    ['breeds', '🧬 Breeds'],
    ['accounts', '💳 Accounts'], ['finance-subtypes', '💰 Expense Categories'], ['expense-groups', '🗂️ Expense Groups'],
    ['other-revenue-subtypes', '💵 Other Revenue Categories'], ['projects', '📁 Projects'],
    ['period-lock', '🔒 Period Lock'],
    ['items', '📦 Items'], ['item-categories', '📂 Item Categories'],
    ['activities', '✅ Activities'], ['operation-types', '⚙️ Operation Types'],
    ['mortality-causes', '🕊️ Mortality Causes'],
    ['general', '⚙️ General'], ['benchmarks', '⚙ Thresholds'],
    ['sanity-rules', '🧪 Sanity Rules'], ['formatting', '🌐 Formatting'],
    // Injected only when is_admin (SettingsPage's `groups` block). Listed so the key space
    // is complete; excluded from the matrix because is_admin — not visible_pages — is what
    // decides them, and a tick here could not turn them on for a non-admin.
    ['category-capabilities', '🧩 Category Capabilities', true],
    ['return-reasons',        '↩️ Sale Return Reasons',   true],
    ['doc-general',           '📄 Documents: General settings', true],
    ['numbering',             '🔢 Numbering',             true],
    ['credit-notes-refunds',  '🧾 Credit Notes & Refunds', true],
    ['print-documents',       '🖨️ Print & Documents',     true],
    ['admin',                 '🔐 Admin & Permissions',   true],
    ['alerts',                '🚨 Alerts',                 true],
    ['roles',                 '🧑‍🤝‍🧑 Roles & Pages',      true],
    ['users',                 '👤 Users',                 true],
  ],
};

// ── LEVEL 3 — the individual reports under each Reports group.
//
// Reports is three groups of leaf reports, and until now only the groups were grantable: a role
// could see "Analytics" or not, never "Analytics but only Project Cost". These are the 26 leaves.
//
// Keys are `reports:<group>:<report id>`, the same `parent + ':' + id` shape used one level up, and
// pageVisible already walks the parent chain to any depth — so nothing in the access logic changes.
//
// ⚠ RESTATED, NOT IMPORTED — the same drift the tab lists carry. TX_TABS / ANALYTICS_TABS /
// FINANCE_TABS are inline consts inside pages/ReportsPage.jsx (two of them declared INSIDE the
// component), so they cannot be imported without exporting them and risking a cycle. Add a report
// there and it will not appear in the matrix until it is added here too. The FE filter fails OPEN
// for an unknown leaf (an unregistered report inherits its group) so a new report is never
// invisible — it is merely ungrantable until listed.
const REPORT_LEAVES = {
  'reports:transactions': [
    ['Production', 'Production'], ['Sales', 'Sales'], ['Consumption', 'Consumption'],
    ['Mortality', 'Mortality'], ['Purchase', 'Purchase'], ['Stock Operations', 'Stock Operations'],
    ['Finance', 'Finance'],
  ],
  'reports:analytics': [
    ['running-stock', 'Running Stock'], ['stock-on-hand', 'Stock On Hand'],
    ['inventory-by-node', 'Inventory by Node'], ['eggs-freshness', 'Egg Freshness'],
    ['eggs-fifo', 'Egg FIFO'], ['band-lifecycle', 'Band Lifecycle'],
    ['bands-history', 'Bands Summary'], ['band-profitability', 'Band Profitability'],
    ['customer-profitability', 'Customer Profitability'],
    ['band-lifecycle-pnl', 'Band Lifecycle Profitability'], ['aged-balances', 'Aged Balances'],
    ['farm-activity', 'Farm Activity'], ['project-cost', 'Project Cost'],
  ],
  'reports:finance': [
    ['financial-statements', 'Financial Statements'], ['trial-balance', 'Trial Balance'],
    ['balance-sheet', 'Balance Sheet'], ['inventory-valuation', 'Inventory Valuation'],
    ['cost-allocation', 'Cost Allocation'], ['sales-allocation', 'Sales Allocation'],
    ['revenue-allocation', 'Other Revenue Allocation'],
  ],
};

// ── The flat tree: pages first, each immediately followed by its own tabs (and, for Reports, each
// group followed by its own reports), so the array order IS the display order and the matrix can
// render it without sorting.
export const PAGE_REGISTRY = PAGES.flatMap(p => [
  // GES-257 (41142c) — was the literal `adminOnly: false`. Only TABS could be adminOnly before, so
  // the flag meant one thing at level 2 and nothing at level 1. It now means the same thing at both:
  // "is_admin decides this surface, not visible_pages". MATRIX_ROWS (below) already excludes such
  // rows from the role×page editor, which is why a page marked here is correctly un-tickable.
  { page_key: p.page_key, label: p.label, parent: null, detail: !!p.detail, adminOnly: !!p.adminOnly },
  ...(TABS[p.page_key] || []).flatMap(([id, label, adminOnly]) => {
    const tabKey = `${p.page_key}:${id}`;
    return [
      { page_key: tabKey, label, parent: p.page_key, detail: !!p.detail, adminOnly: !!adminOnly },
      ...(REPORT_LEAVES[tabKey] || []).map(([lid, llabel]) => ({
        page_key: `${tabKey}:${lid}`,
        label: llabel,
        parent: tabKey,
        detail: !!p.detail,
        adminOnly: !!adminOnly,
        leaf: true,
      })),
    ];
  }),
]);

// The rows the matrix editor governs: everything except the is_admin-gated surfaces.
export const MATRIX_ROWS = PAGE_REGISTRY.filter(r => !r.adminOnly);

export const ALL_PAGE_KEYS = PAGE_REGISTRY.map(r => r.page_key);

const BY_KEY = new Map(PAGE_REGISTRY.map(r => [r.page_key, r]));
export const getRegistryEntry = (key) => BY_KEY.get(key) || null;
export const isKnownPageKey   = (key) => BY_KEY.has(key);
// GES-257 (41142c) — "is this surface decided by is_admin rather than by visible_pages?".
// Fails CLOSED on an unknown key in the only direction that matters: an unknown key is not
// adminOnly, but it is also not in the registry, so pageVisible already refuses it.
export const isAdminOnly      = (key) => !!(BY_KEY.get(key) || {}).adminOnly;

// Top-level rows (parent === null), in menu order.
export const REGISTRY_PAGES = PAGE_REGISTRY.filter(r => r.parent === null);
// The tabs of one page, in their bar order.
export const tabsOf = (pageKey) => PAGE_REGISTRY.filter(r => r.parent === pageKey);

// visible_pages carries this sentinel to mean "every page, present and future". The server
// sends it for the is_system Admin role and for any role stored with it; the matrix cannot
// represent it as ticks, so screens must handle it explicitly rather than silently expand it.
export const WILDCARD = '*';
export const hasWildcard = (visiblePages) => !!visiblePages && visiblePages[WILDCARD] === true;
